import { membershipIdFromFrameShape } from './frameMembership.js';

export const MEMBERSHIP_PIN_TYPES = new Set(['pin-card', 'suggest-pin', 'conversation-pin']);

export function isMembershipPinType(type) {
  return MEMBERSHIP_PIN_TYPES.has(String(type || ''));
}

function findFrameForMembershipId(editor, frameId) {
  const key = String(frameId || '').trim();
  if (!key || !editor?.getCurrentPageShapes) return null;
  return (editor.getCurrentPageShapes() || []).find((candidate) => (
    candidate?.type === 'frame'
    && String(membershipIdFromFrameShape(candidate) || '') === key
  )) || null;
}

/** Current cluster for a pin-card / AI pin, from meta.frameId or frame parent. */
export function clusterForPinShape(editor, shape) {
  if (!editor || !shape || !isMembershipPinType(shape.type)) return null;
  const metaFrameId = String(shape.meta?.frameId || '').trim();
  let frame = metaFrameId ? findFrameForMembershipId(editor, metaFrameId) : null;
  if (!frame && shape.parentId) {
    const parent = editor.getShape?.(shape.parentId);
    if (parent?.type === 'frame') frame = parent;
  }
  if (!frame) return null;
  const frameId = membershipIdFromFrameShape(frame) || String(frame.id);
  const clusterLabel = String(frame.props?.name || frame.props?.label || '').trim();
  return {
    frameId,
    ...(clusterLabel ? { clusterLabel } : {}),
  };
}

/** Compact snapshot used to detect membership / rename changes. */
export function readClusterMembership(editor, shape) {
  if (!isMembershipPinType(shape?.type)) return null;
  const cluster = clusterForPinShape(editor, shape) || {};
  const activityId = String(shape.meta?.activityId || '').trim();
  return {
    shapeId: String(shape.id),
    activityId,
    frameId: String(cluster.frameId || '').trim(),
    clusterLabel: String(cluster.clusterLabel || '').trim(),
  };
}

export function membershipSnapshotKey(snapshot) {
  if (!snapshot) return '';
  return [
    snapshot.shapeId || '',
    snapshot.activityId || '',
    snapshot.frameId || '',
    snapshot.clusterLabel || '',
  ].join('|');
}

export function pinsInFrame(editor, frameId) {
  const key = String(frameId || '').trim();
  if (!editor?.getCurrentPageShapes || !key) return [];
  return (editor.getCurrentPageShapes() || []).filter((shape) => {
    if (!isMembershipPinType(shape?.type)) return false;
    const cluster = clusterForPinShape(editor, shape);
    return String(cluster?.frameId || '') === key;
  });
}

export function pinsWithStaleFrameId(editor, frameId) {
  const key = String(frameId || '').trim();
  if (!editor?.getCurrentPageShapes || !key) return [];
  return (editor.getCurrentPageShapes() || []).filter((shape) => {
    if (!isMembershipPinType(shape?.type)) return false;
    return String(shape.meta?.frameId || '').trim() === key;
  });
}

function activityById(activities, activityId) {
  const needle = String(activityId || '');
  return (activities || []).find(a => String(a?._id || a?.id || a?.activityId || '') === needle) || null;
}

function pinEvidence(shape, activity) {
  const comment = activity?.action || activity?.comment || {};
  const highlight = comment?.highlightData || {};
  const content = activity?.content?.[0] || {};
  const raw = [
    comment.text || comment.body || '',
    highlight.selectionText || highlight.ocrText || '',
    content.title || '',
    shape?.props?.title || '',
    shape?.props?.snippet || '',
    shape?.meta?.intentSummary || '',
  ].map(part => String(part || '').trim()).filter(Boolean);
  const intentSummary = String(highlight.intentSummary || raw[0] || '').trim().replace(/\s+/g, ' ').slice(0, 600);
  const label = String(content.title || shape?.props?.title || intentSummary || '').trim().slice(0, 80);
  return { intentSummary, label };
}

/**
 * Re-assert a pin's digest row with its current cluster (or none).
 * Same-source add updates the ledger row; leaving a frame is a modify, not a delete.
 */
export function buildPinMembershipAddEvent(editor, shape, activities = [], at = Date.now()) {
  if (!isMembershipPinType(shape?.type)) return null;
  const activityId = String(shape.meta?.activityId || '').trim();
  const activity = activityById(activities, activityId);
  const { intentSummary, label } = pinEvidence(shape, activity);
  const cluster = clusterForPinShape(editor, shape);
  const event = {
    op: 'add',
    label: label || activityId || String(shape.id),
    intentSummary,
    source: 'cluster-membership',
    at,
  };
  if (activityId) event.pinId = activityId;
  if (shape.type !== 'pin-card' || !activityId) event.shapeId = String(shape.id);
  if (cluster?.frameId) event.frameId = cluster.frameId;
  if (cluster?.clusterLabel) event.clusterLabel = cluster.clusterLabel;
  if (!event.pinId && !event.shapeId) return null;
  return event;
}

/** Revoke cluster-about claims for a deleted frame without dropping pin facts. */
export function buildFrameDeleteEvent(frameShape, at = Date.now()) {
  if (!frameShape || frameShape.type !== 'frame') return null;
  const frameId = membershipIdFromFrameShape(frameShape) || String(frameShape.id);
  const label = String(frameShape.props?.name || frameShape.props?.label || frameId).trim();
  return {
    op: 'delete',
    shapeId: String(frameShape.id),
    frameId: String(frameId),
    label: label.slice(0, 80),
    intentSummary: '',
    at,
  };
}
